import React, { createContext, useState, useContext, ReactNode, useEffect, useCallback } from 'react';
import { AdminUser } from '../components/settings/interface';
import { getAdmin } from '../services/adminService';
import { useNavigate, useLocation } from 'react-router-dom';

interface AuthContextType {
    isAuthenticated: boolean;
    user: AdminUser | null;
    setLogin: (userToken: string) => Promise<void>;
    doLogout: () => void;
    isLoading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [token, setToken] = useState<string | null>(null);
    const [user, setUser] = useState<AdminUser | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const navigate = useNavigate();
    const location = useLocation();

    const fetchUser = useCallback(async () => {
        try {
            const { success, data } = await getAdmin();
            console.log(data);
            if (success) {
                setUser(data);
            } else {
                doLogout();
            }
        } catch (error: any) {
            if (!error.response && location.pathname !== '/network-error') {
                navigate('/network-error');
            } else {
                console.error('Error fetching user data:', error);
            }
        }
    }, [location.pathname, navigate]);

    useEffect(() => {
        const initializeAuth = async () => {
            const authToken = localStorage.getItem('authToken');
            
            if (location.pathname === '/network-error' || location.pathname === '/forgot-password') {
                setIsLoading(false);
                return;
            }

            if (authToken) {
                setToken(authToken);
                await Promise.all([
                    fetchUser(),
                ]);
            } else {
                navigate('/auth');
            }
            setIsLoading(false);
        };

        initializeAuth();
    }, [navigate, location.pathname, fetchUser]);

    const setLogin = async (authToken: string) => {
        localStorage.setItem('authToken', authToken);
        setToken(authToken);
        await Promise.all([
            fetchUser(),
        ]);
    };

    const doLogout = () => {
        localStorage.removeItem('authToken');
        setToken(null);
        setUser(null);
    };

    const isAuthenticated = !!token;

    return (
        <AuthContext.Provider value={{ isAuthenticated, user, setLogin, doLogout, isLoading }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

export default AuthContext;